import { observer } from 'mobx-react';
import React, { useEffect, useState, useRef } from 'react';
import { ClaimEditorStore } from '../../stores/claim-editor';
import { BaseButton } from '../base/button';
import { Spinner } from '../base/spinner';
import { BaseDialog } from '../base/dialog/dialog';
import { AdditionalInformationForm } from './forms/additional-information-form';
import { AdditionalProvidersForm } from './forms/additional-providers-form';
import { BillingProviderForm } from './forms/additional-providers-form/billing-provider-form';
import { FacilityForm } from './forms/facility-form';
import { PatientForm } from './forms/patient-form';
import { SubscriberPayerList } from './forms/subscriber-payer-form';
import { DiagnosisCodesForm } from './forms/diagnosis-codes-form/diagnosis-codes-form';
import { ServicesList } from './forms/services-form/services-list';
import { GuarantorForm } from './forms/guarantor-form';
import { SettingsForm } from './forms/settings-form';
import * as S from './claim-editor.styles';
import * as ErrorStyles from '../../services/auth/error.styles';

interface IClaimEditorProps {
    claimEditorStore: ClaimEditorStore;
    claimId?: string;
    isTemplate?: boolean;
    onCancel?: () => void;
    onSuccess?: (claimId: string) => void;
    disabledSections?: string[];
    customSections?: React.ReactNode;
    headerActions?: React.ReactNode;
}

export const ClaimEditor: React.FunctionComponent<IClaimEditorProps> = observer((props) => {
    const {
        claimEditorStore,
        claimId,
        isTemplate = false,
        onCancel,
        onSuccess,
        disabledSections = [],
        customSections,
        headerActions,
    } = props;

    const hasClaimIdParam = Boolean(claimId);
    const isCreateMode = claimEditorStore?.isCreateClaim ?? !hasClaimIdParam;
    const isEditMode = !isCreateMode;
    const pageTitle = isCreateMode ? 'Create Claim' : 'Edit Claim';
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const textRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        const loadData = async () => {
            if (hasClaimIdParam && claimId && claimEditorStore) {
                await claimEditorStore.loadClaimData(claimId, isTemplate);
            }
        };

        loadData();

        // Preload payers on mount
        if (claimEditorStore && !(claimEditorStore as any)['payersLoaded']) {
            (claimEditorStore as any).preloadPayers();
        }

        // Cleanup on unmount
        return () => {
            claimEditorStore?.resetStore();
        };
    }, [claimId, hasClaimIdParam, claimEditorStore, isTemplate]);

    const handleSubmit = async () => {
        if (!claimEditorStore) return;
        // Trigger validation in all child forms so banners/errors appear
        claimEditorStore.triggerValidation();
        if (!claimEditorStore.canSubmit) {
            const reasons = claimEditorStore.getSubmitBlockers();
            const message = reasons && reasons.length ? `Cannot submit:\n- ${reasons.join('\n- ')}` : 'Please complete all required fields before submitting.';
            setErrorMessage(message);
            return;
        }
        try {
            const newId = await claimEditorStore.submitClaim();
            if (onSuccess && newId) {
                onSuccess(newId);
            }
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'An error occurred while submitting the claim.';
            setErrorMessage(errorMsg);
        }
    };

    const handleCloseError = () => {
        setErrorMessage(null);
    };

    const handleContinue = () => {
        handleCloseError();
    };

    const renderActions = () => {
        return (
            <ErrorStyles.DialogActions>
                <ErrorStyles.DialogButton variant="primary" onClick={handleContinue}>
                    Close
                </ErrorStyles.DialogButton>
            </ErrorStyles.DialogActions>
        );
    };

    const selectAll = () => {
        if (textRef && textRef.current) {
            textRef.current.select();
        }
    };

    if (claimEditorStore?.isLoading) {
        return (
            <S.LoadingContainer>
                <Spinner />
            </S.LoadingContainer>
        );
    }

    return (
        <S.Container>
            <S.Header>
                <S.Title>{pageTitle}</S.Title>
                {headerActions}
            </S.Header>

            <S.Content>
                {/* Patient Information Section */}
                {!disabledSections.includes('patient') && (
                    <S.Section>
                        <S.SectionTitle>Patient Information</S.SectionTitle>
                        <PatientForm claimEditorStore={claimEditorStore} />
                    </S.Section>
                )}

                {/* Guarantor Information Section */}
                {!disabledSections.includes('guarantor') && (
                    <S.Section>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                            <S.SectionTitle>Guarantor Information</S.SectionTitle>
                            <BaseButton
                                type="button"
                                variant="outlined"
                                onClick={(): void => {
                                    claimEditorStore.copyGuarantorFromPatient();
                                }}
                                disabled={!claimEditorStore?.patient}
                            >
                                Copy From Patient
                            </BaseButton>
                        </div>
                        <GuarantorForm claimEditorStore={claimEditorStore} />
                    </S.Section>
                )}

                {/* Subscriber Information Section */}
                {!disabledSections.includes('subscriber') && (
                    <S.Section>
                        <S.SectionTitle>Subscriber Information</S.SectionTitle>
                        <SubscriberPayerList 
                            initialItems={(claimEditorStore.activePayload as any)?.subscribers}
                            claimEditorStore={claimEditorStore}
                        />
                    </S.Section>
                )}

                {/* Diagnosis Codes Section */}
                <S.Section>
                    <S.SectionTitle>Diagnosis Codes</S.SectionTitle>
                    <DiagnosisCodesForm claimEditorStore={claimEditorStore} />
                </S.Section>

                {/* Services Section */}
                <S.Section>
                    <S.SectionTitle>Services</S.SectionTitle>
                    <ServicesList claimEditorStore={claimEditorStore} />
                </S.Section>

                {/* Provider Information Section */}
                {!disabledSections.includes('provider') && (
                    <S.Section>
                        <S.SectionTitle>Billing Provider Information</S.SectionTitle>
                        <BillingProviderForm claimEditorStore={claimEditorStore} />
                    </S.Section>
                )}

                {/* Facility Information Section */}
                {!disabledSections.includes('facility') && (
                    <S.Section>
                        <S.SectionTitle>Facility Information</S.SectionTitle>
                        <FacilityForm claimEditorStore={claimEditorStore} />
                    </S.Section>
                )}

                {/* Additional Providers Section */}
                {!disabledSections.includes('additionalProviders') && (
                    <S.Section>
                        <S.SectionTitle>Additional Providers</S.SectionTitle>
                        <AdditionalProvidersForm claimEditorStore={claimEditorStore} />
                    </S.Section>
                )}

                {/* Additional Information Section */}
                {!disabledSections.includes('additionalInformation') && (
                    <S.Section>
                        <S.SectionTitle>Additional Information</S.SectionTitle>
                        <AdditionalInformationForm 
                            claimEditorStore={claimEditorStore} 
                            isEditMode={isEditMode} 
                        />
                    </S.Section>
                )}

                {/* Settings Section */}
                {!disabledSections.includes('settings') && (
                    <S.Section>
                        <S.SectionTitle>Settings</S.SectionTitle>
                        <SettingsForm claimEditorStore={claimEditorStore} />
                    </S.Section>
                )}

                {/* Custom sections provided by the app */}
                {customSections}

                {/* Submit Button */}
                <S.Section>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <BaseButton onClick={handleSubmit} disabled={claimEditorStore?.isLoading}>
                            {claimEditorStore?.isLoading
                                ? 'Submitting…'
                                : claimEditorStore?.isCreateClaim
                                    ? 'Create Claim'
                                    : 'Edit Existing Claim'}
                        </BaseButton>
                        {onCancel && (
                            <BaseButton onClick={onCancel}>
                                Cancel
                            </BaseButton>
                        )}
                    </div>
                </S.Section>
            </S.Content>
            {errorMessage && (
                <BaseDialog
                    open={true}
                    disableBackdropClick={true}
                    disableEscapeKeyDown={true}
                    renderTitle="Cannot submit claim"
                    renderActions={renderActions()}>
                    <ErrorStyles.DialogBody>
                        <ErrorStyles.Col>
                            {errorMessage}
                            <ErrorStyles.Textarea
                                ref={textRef}
                                rows={11}
                                value={errorMessage}
                                onClick={selectAll}
                                readOnly={true}
                            />
                        </ErrorStyles.Col>
                    </ErrorStyles.DialogBody>
                </BaseDialog>
            )}
        </S.Container>
    );
});

